import type { AudienceSegment } from '@/context/PreferencesContext';

const API_BASE = '/api/proxy';

export interface OnboardingData {
  displayName?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  gender?: string;
  /** Admin-managed education level tier slug (e.g. 'sma', 'smp'). Validated server-side. */
  educationLevelSlug?: string;
  /** null explicitly clears it (gradeless tier); omit to leave untouched. */
  currentGrade?: string | null;
  schoolName?: string;
  /** @deprecated transition-period scalar — prefer targetExams. ExamType slug, or null to explicitly clear. Validated + normalized server-side. */
  targetExam?: string | null;
  /** ExamType slugs (multi-select). Validated + normalized server-side. */
  targetExams?: string[];
  targetUniversity?: string;
  targetMajor?: string;
  referralSource?: string;
  province?: string;
  city?: string;
  /** Coarse audience segment — drives SegmentExamTypePolicy. */
  audienceSegment?: AudienceSegment | null;
  /** Self-reported birth YEAR (2026-09-28 marketing-consent plan) — only sent
   * alongside whatsappMarketingConsent from IdentityStep's revealed field. */
  birthYear?: number;
  /** WhatsApp marketing opt-in from IdentityStep. Only sent when the user
   * explicitly ticked/unticked the checkbox; undefined leaves it untouched. */
  whatsappMarketingConsent?: boolean;
}

export interface OnboardingResult {
  success: boolean;
  /** Present only when a granted:true whatsappMarketingConsent choice this
   * call made was refused (age gate) — onboarding still completed. */
  marketingConsentRefusalReason?: 'NOT_ADULT';
}

export interface ProfileUpdateData {
  displayName?: string;
  avatarUrl?: string;
  bio?: string;
  phoneNumber?: string;
  currentGrade?: string;
  schoolName?: string;
  targetUniversity?: string;
  targetMajor?: string;
}

export interface PersonalizationUpdateData {
  /** Omit to leave untouched, pass null to explicitly clear. */
  audienceSegment?: AudienceSegment | null;
  /** @deprecated transition-period scalar — prefer targetExams. ExamType slug, or null to explicitly clear. Validated server-side. */
  targetExam?: string | null;
  /** ExamType slugs (multi-select), or an empty array to explicitly clear. Validated server-side. */
  targetExams?: string[];
}

export interface PersonalizationUpdateResult {
  success: boolean;
  profile: {
    audienceSegment: AudienceSegment | null;
    /** @deprecated transition-period scalar mirror of targetExams[0] — prefer targetExams. */
    targetExam: string | null;
    targetExams: string[];
  };
}

// Shape of GET /users/me — see user.routes.ts's `/me` handler (apps/api) for the
// source of these fields. `profile`/`student` mirror the Prisma Profile/Student
// rows (nullable — a brand-new user has neither yet) and only carry the fields
// AuthContext/OnboardingGuard actually read; the API sends more (identities,
// role, status, etc.) that nothing here consumes yet.
export interface UserProfileResponse {
  onboardingCompleted: boolean;
  profile: {
    /** @deprecated transition-period scalar — prefer targetExams. */
    targetExam: string | null;
    targetExams: string[];
    audienceSegment: AudienceSegment | null;
  } | null;
  student: {
    currentGrade: string | null;
    educationLevelSlug: string | null;
    educationLevel: { slug: string; name: string } | null;
    schoolName: string | null;
  } | null;
}

async function getToken(): Promise<string | null> {
  try {
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) return user.getIdToken();
  } catch {}
  return null;
}

async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const token = await getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${url}`, { ...options, headers });
  if (!res.ok) {
    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `HTTP ${res.status}`);
    }
    // Non-JSON response (e.g. Cloudflare error page) — return a user-friendly message
    if (res.status === 503) throw new Error('Layanan sedang tidak tersedia. Silakan coba lagi dalam beberapa saat.');
    if (res.status >= 500) throw new Error('Terjadi kesalahan pada server. Silakan coba lagi.');
    throw new Error(`HTTP ${res.status}`);
  }
  return res;
}

// Shared in-flight GET /users/me — see getProfile below for why.
let inFlightProfile: Promise<UserProfileResponse | null> | null = null;

export const ProfileService = {
  /** Complete onboarding — saves all profile fields + marks onboarding as done */
  async completeOnboarding(data: OnboardingData): Promise<OnboardingResult> {
    const res = await fetchWithAuth('/users/me/onboarding', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.json();
  },

  /** Update profile fields (after onboarding) */
  async updateProfile(data: ProfileUpdateData): Promise<{ success: boolean }> {
    const res = await fetchWithAuth('/users/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return res.json();
  },

  /** Self-service audience segment / exam interest update ("Ubah fokus" edit control). */
  async updatePersonalization(data: PersonalizationUpdateData): Promise<PersonalizationUpdateResult> {
    const res = await fetchWithAuth('/users/me/personalization', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return res.json();
  },

  /**
   * Get full user profile including onboarding status.
   *
   * AuthContext and OnboardingGuard both call this on mount, so every dashboard load
   * fired two identical GET /users/me requests in the same tick — measured on staging at
   * 112ms and 124ms, side by side. Sharing the in-flight promise collapses them into one.
   *
   * Deliberately an in-flight dedupe and NOT a TTL cache: the entry clears the moment the
   * request settles, so a PATCH /users/me followed by a re-read (settings, profile) still
   * hits the network and sees its own write.
   */
  async getProfile(): Promise<UserProfileResponse | null> {
    if (inFlightProfile) return inFlightProfile;

    inFlightProfile = fetchWithAuth('/users/me')
      .then((res) => res.json())
      .finally(() => {
        inFlightProfile = null;
      });

    return inFlightProfile;
  },
};
